import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import React, { useEffect } from "react";
import { Animated } from "react-native";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../../useAppTheme";
import Box from "../../Box";
import type { IconType } from "../../TextLoop";
import MarkdownText from "../MarkdownText";

export type Props = {
	toolCallingIcon: IconType;
	toolCallingState: "completed" | "running" | "failed";
	text: string;
	content: string;
};

const ToolCallingMessage = ({
	toolCallingState,
	text,
	content,
	toolCallingIcon,
}: Props) => {
	const theme = useAppTheme();
	const checkScale = React.useRef(new Animated.Value(0)).current;

	const animatedValue = React.useRef(new Animated.Value(0)).current;
	useEffect(() => {
		let animation: Animated.CompositeAnimation | null = null;

		if (toolCallingState === "completed") {
			animation = Animated.timing(checkScale, {
				toValue: 1,
				duration: 400,
				useNativeDriver: true,
			});
			animation.start();
		} else {
			checkScale.setValue(0);
		}

		return () => {
			animation?.stop();
		};
	}, [toolCallingState, checkScale]);
	useEffect(() => {
		const animation = Animated.loop(
			Animated.sequence([
				Animated.timing(animatedValue, {
					toValue: 1,
					duration: 1000,
					useNativeDriver: false,
				}),
				Animated.timing(animatedValue, {
					toValue: 0,
					duration: 1000,
					useNativeDriver: false,
				}),
			]),
		);

		animation.start();

		return () => {
			animation.stop();
		};
	}, [animatedValue]);
	return (
		<Box paddingRight={16} paddingVertical={8} paddingLeft={2}>
			<Box alignItems="center" flexDirection="row" paddingVertical={4} gap={8}>
				{toolCallingState === "running" && (
					<>
						<Icon
							name={toolCallingIcon}
							size={20}
							color={theme.colors.onSurfaceVariant}
						/>

						<Box flexDirection="row">
							<Animated.Text
								style={{
									color: theme.colors.onSurfaceVariant,
									opacity: animatedValue.interpolate({
										inputRange: [0, 1],
										outputRange: [0.8, 0.2],
									}),
								}}
							>
								{text.slice(0, Math.ceil(text.length / 4))}
							</Animated.Text>
							<Animated.Text
								style={{
									color: theme.colors.onSurfaceVariant,
									opacity: animatedValue.interpolate({
										inputRange: [0, 1],
										outputRange: [0.6, 0.4],
									}),
								}}
							>
								{text.slice(
									Math.ceil(text.length / 4),
									Math.ceil(text.length / 2),
								)}
							</Animated.Text>
							<Animated.Text
								style={{
									color: theme.colors.onSurfaceVariant,
									opacity: animatedValue.interpolate({
										inputRange: [0, 1],
										outputRange: [0.4, 0.6],
									}),
								}}
							>
								{text.slice(
									Math.ceil(text.length / 2),
									Math.ceil((text.length * 3) / 4),
								)}
							</Animated.Text>
							<Animated.Text
								style={{
									color: theme.colors.onSurfaceVariant,
									opacity: animatedValue.interpolate({
										inputRange: [0, 1],
										outputRange: [0.2, 0.8],
									}),
								}}
							>
								{text.slice(Math.ceil((text.length * 3) / 4))}
							</Animated.Text>
						</Box>
					</>
				)}

				{toolCallingState === "completed" && (
					<>
						<Box
							width={20}
							height={20}
							justifyContent="center"
							alignItems="center"
						>
							<Animated.View
								style={{
									backgroundColor: theme.colors.primary,
									borderRadius: 100,
									justifyContent: "center",
									alignItems: "center",
									width: 16,
									height: 16,
									transform: [
										{
											scale: checkScale.interpolate({
												inputRange: [0, 1],
												outputRange: [0.4, 1],
											}),
										},
									],
									opacity: checkScale,
								}}
							>
								<Icon name="check" size={15} color={theme.colors.onPrimary} />
							</Animated.View>
						</Box>
						<Text
							variant="bodyMedium"
							style={{ color: theme.colors.onSurfaceVariant }}
						>
							{text}
						</Text>
					</>
				)}
			</Box>
			<MarkdownText
				text={content}
				variant="explanation"
				style={{ color: theme.colors.onSurfaceVariant }}
			/>
		</Box>
	);
};

export default ToolCallingMessage;
